import type { PageHelpFlow } from './types.js';

type Steps = PageHelpFlow['steps'];

const SALES: Steps = [
  { id: 'quote', label: 'پیش‌فاکتور' },
  { id: 'order', label: 'سفارش فروش' },
  { id: 'delivery', label: 'رزرو و تحویل کالا' },
  { id: 'invoice', label: 'فاکتور فروش' },
  { id: 'finance', label: 'بررسی و ثبت در مالی' },
];

const PURCHASE: Steps = [
  { id: 'need', label: 'اعلام نیاز' },
  { id: 'request', label: 'درخواست خرید' },
  { id: 'approval', label: 'بررسی درخواست' },
  { id: 'order', label: 'سفارش خرید' },
  { id: 'receipt', label: 'دریافت کالا' },
];

const STOCK: Steps = [
  { id: 'definitions', label: 'تعریف کالا و محل' },
  { id: 'receipt', label: 'ورود کالا' },
  { id: 'stock', label: 'موجودی و رزرو' },
  { id: 'issue', label: 'خروج کالا' },
  { id: 'review', label: 'بررسی گردش و مانده' },
];

const PROJECT: Steps = [
  { id: 'project', label: 'تعریف پروژه' },
  { id: 'team', label: 'تعیین تیم' },
  { id: 'task', label: 'تعریف و واگذاری کار' },
  { id: 'execution', label: 'اجرای کار' },
  { id: 'review', label: 'بررسی نتیجه و پیشرفت' },
];

const COUNT: Steps = [
  { id: 'plan', label: 'انتخاب انبار و تاریخ' },
  { id: 'snapshot', label: 'ایجاد جلسهٔ شمارش' },
  { id: 'count', label: 'ثبت شمارش واقعی' },
  { id: 'variance', label: 'بررسی کسری و اضافی' },
  { id: 'post', label: 'تأیید نهایی و تعدیل' },
];

const CALL: Steps = [
  { id: 'source', label: 'دریافت یا بارگذاری تماس' },
  { id: 'identify', label: 'شناسایی و بررسی تماس' },
  { id: 'recording', label: 'بررسی صوت و متن موجود' },
  { id: 'analysis', label: 'تحلیل مجاز مکالمه' },
  { id: 'followup', label: 'پیگیری مخاطب' },
];

function at(
  id: string,
  title: string,
  steps: Steps,
  currentStepId: string,
  position: string,
  before: string,
  after: string,
  role: 'stage' | 'support' = 'stage',
  note?: string,
): PageHelpFlow {
  return {
    id,
    title,
    steps,
    role,
    currentStepId,
    position,
    before,
    after,
    ...(note ? { note } : {}),
  };
}

function overview(
  id: string,
  title: string,
  steps: Steps,
  position: string,
  before: string,
  after: string,
  note?: string,
): PageHelpFlow {
  return { id, title, steps, role: 'overview', position, before, after, ...(note ? { note } : {}) };
}

interface ReferenceFlow {
  readonly title: string;
  readonly steps: Steps;
  readonly step: string;
  readonly prerequisite: string;
  readonly handoff: string;
}

interface ReferenceEntity {
  readonly kind: string;
  readonly label: string;
  readonly check: string;
  readonly flows: readonly ReferenceFlow[];
}

/** Reference records support several processes; they are not financial/inventory transactions. */
const REFERENCES: readonly ReferenceEntity[] = [
  {
    kind: 'party',
    label: 'طرف‌حساب',
    check: 'هویت، مشخصات تماس و نقش مشتری یا تأمین‌کننده',
    flows: [
      {
        title: 'شناخت مشتری تا فروش',
        step: 'party',
        steps: [{ id: 'party', label: 'شناخت مشتری' }, ...SALES.slice(0, 4)],
        prerequisite:
          'هویت مشتری و راه تماس او باید روشن باشد؛ ابتدا وجود پروندهٔ قبلی را بررسی کن.',
        handoff:
          'همین طرف‌حساب در پیش‌فاکتور و سفارش فروش انتخاب می‌شود و سوابقش در پروندهٔ مشتری دنبال می‌شود.',
      },
      {
        title: 'شناخت تأمین‌کننده تا دریافت خرید',
        step: 'party',
        steps: [
          { id: 'party', label: 'تعریف تأمین‌کننده' },
          { id: 'need', label: 'اعلام نیاز خرید' },
          { id: 'order', label: 'سفارش خرید' },
          { id: 'receipt', label: 'دریافت کالا' },
        ],
        prerequisite:
          'مشخصات شخص یا شرکت و نقش تأمین‌کننده را مشخص کن؛ یک شرکت می‌تواند هر دو نقش را داشته باشد.',
        handoff:
          'تأمین‌کنندهٔ تعریف‌شده در سفارش خرید انتخاب می‌شود؛ تعریف او به‌تنهایی خرید یا بدهی ثبت نمی‌کند.',
      },
    ],
  },
  {
    kind: 'product',
    label: 'کالا',
    check: 'نام، کد و واحد سنجش پایهٔ کالا',
    flows: [
      {
        title: 'آماده‌سازی کالا برای فروش',
        step: 'product',
        steps: [{ id: 'product', label: 'تعریف کالا و واحد پایه' }, ...SALES.slice(0, 4)],
        prerequisite:
          'واحد سنجش پایه باید تعریف شده و مشخصات کالا از کالاهای مشابه قابل تشخیص باشد.',
        handoff:
          'همین کالا در اقلام پیش‌فاکتور و سفارش انتخاب می‌شود؛ موجودی و تحویل آن از عملیات انبار بررسی می‌شود.',
      },
      {
        title: 'آماده‌سازی کالا برای تأمین',
        step: 'product',
        steps: [
          { id: 'product', label: 'تعریف کالای موردنیاز' },
          { id: 'request', label: 'درخواست خرید' },
          { id: 'order', label: 'سفارش خرید' },
          { id: 'receipt', label: 'رسید انبار' },
          { id: 'stock', label: 'بررسی موجودی' },
        ],
        prerequisite:
          'نام و واحد پایه باید با کالایی که واقعاً خریداری و شمارش می‌شود سازگار باشد.',
        handoff: 'کالا در درخواست و سفارش خرید استفاده می‌شود؛ ورود واقعی آن با رسید ثبت می‌شود.',
      },
    ],
  },
  {
    kind: 'uom',
    label: 'واحد سنجش',
    check: 'نام و کد واحد و معنای مقدار آن',
    flows: [
      {
        title: 'از واحد سنجش تا مقدار کالای انبار',
        step: 'uom',
        steps: [
          { id: 'uom', label: 'تعریف واحد سنجش' },
          { id: 'product', label: 'انتخاب واحد پایهٔ کالا' },
          { id: 'conversion', label: 'تعریف تبدیل در صورت نیاز' },
          { id: 'movement', label: 'ثبت مقدار در انبار' },
          { id: 'review', label: 'بررسی گردش مقدار' },
        ],
        prerequisite: 'مشخص کن مقدار کالا با عدد، کیلوگرم، بسته یا واحد دیگری سنجیده می‌شود.',
        handoff:
          'واحد در تعریف کالا و عملیات آن انتخاب می‌شود؛ ضریب بین دو واحد در صفحهٔ تبدیل واحدها تعریف می‌شود.',
      },
      {
        title: 'از واحد سنجش تا اقلام فروش',
        step: 'uom',
        steps: [
          { id: 'uom', label: 'تعریف واحد سنجش' },
          { id: 'product', label: 'تعریف کالای قابل فروش' },
          { id: 'quote', label: 'مقدار پیش‌فاکتور' },
          { id: 'order', label: 'مقدار سفارش' },
          { id: 'delivery', label: 'مقدار تحویل' },
        ],
        prerequisite:
          'واحدی را انتخاب کن که مشتری و فروشنده از معنای مقدار آن برداشت یکسانی داشته باشند.',
        handoff:
          'مقدار اقلام فروش با واحد مشخص خوانده می‌شود؛ تعریف واحد به‌تنهایی مقدار یا موجودی نمی‌سازد.',
      },
    ],
  },
  {
    kind: 'tax',
    label: 'مالیات',
    check: 'کد، نام و صورت و مخرج نرخ مورد تأیید مالی',
    flows: [
      {
        title: 'از تعریف نرخ تا اقلام فروش',
        step: 'tax',
        steps: [
          { id: 'tax', label: 'تعریف نرخ معتبر' },
          { id: 'quote', label: 'انتخاب مالیات اقلام' },
          { id: 'order', label: 'بررسی اقلام سفارش' },
          { id: 'invoice', label: 'بررسی فاکتور فروش' },
          { id: 'finance', label: 'بررسی مالی' },
        ],
        prerequisite: 'مسئول مالی باید نرخ موردنیاز و روش ثبت آن را تعیین کرده باشد.',
        handoff: 'نرخ تعریف‌شده در اقلام مجاز انتخاب و نتیجهٔ آن روی سند مربوط بررسی می‌شود.',
      },
    ],
  },
  {
    kind: 'warehouse',
    label: 'انبار',
    check: 'کد، نام و فعال بودن انبار',
    flows: [
      {
        title: 'آماده‌سازی محل نگهداری کالا',
        step: 'warehouse',
        steps: [
          { id: 'warehouse', label: 'تعریف انبار' },
          { id: 'location', label: 'تعریف مکان‌های داخلی' },
          { id: 'receipt', label: 'ثبت ورود کالا' },
          { id: 'stock', label: 'بررسی موجودی' },
          { id: 'issue', label: 'ثبت خروج کالا' },
        ],
        prerequisite:
          'محل اصلی نگهداری کالا باید مشخص باشد؛ انبار را از قفسه یا سالن داخلی آن جدا کن.',
        handoff:
          'مکان‌های این انبار تعریف می‌شوند و در رسید، خروج و انتقال، انبار و محل مناسب انتخاب می‌شود.',
      },
      {
        title: 'تعیین مقصد خرید',
        step: 'warehouse',
        steps: [
          { id: 'warehouse', label: 'تعریف انبار مقصد' },
          { id: 'request', label: 'اعلام نیاز خرید' },
          { id: 'order', label: 'سفارش خرید' },
          { id: 'receipt', label: 'دریافت در محل درست' },
          { id: 'review', label: 'بررسی ماندهٔ انبار' },
        ],
        prerequisite: 'انبار مقصد دریافت خرید و مسئول تحویل‌گیری باید برای همکاران روشن باشد.',
        handoff:
          'در مراحل مجاز خرید و رسید، مقصد درست انتخاب می‌شود؛ ساختن انبار به‌تنهایی کالایی وارد آن نمی‌کند.',
      },
    ],
  },
  {
    kind: 'location',
    label: 'مکان انبار',
    check: 'انبار مربوط، کد و نام مکان داخلی',
    flows: [
      {
        title: 'از انبار تا محل دقیق موجودی',
        step: 'location',
        steps: [
          { id: 'warehouse', label: 'تعریف انبار' },
          { id: 'location', label: 'تعریف محل نگهداری' },
          { id: 'receipt', label: 'ورود به محل' },
          { id: 'stock', label: 'موجودی همان محل' },
          { id: 'issue', label: 'خروج از محل درست' },
        ],
        prerequisite:
          'انبارِ مربوط باید از قبل تعریف شده باشد؛ نام مکان را با محل واقعی آن تطبیق بده.',
        handoff:
          'در عملیات انبار، مکان متعلق به همان انبار انتخاب می‌شود تا محل دقیق کالا قابل پیگیری بماند.',
      },
      {
        title: 'محل مبدأ و مقصد انتقال',
        step: 'location',
        steps: [
          { id: 'location', label: 'تعریف مکان‌های دو انبار' },
          { id: 'transfer', label: 'ثبت انتقال' },
          { id: 'dispatch', label: 'خروج از مبدأ' },
          { id: 'receive', label: 'وصول در مقصد' },
          { id: 'review', label: 'بررسی موجودی دو طرف' },
        ],
        prerequisite:
          'هر مکان باید به انبار واقعی خودش مربوط باشد؛ نام مشابه به معنی مکان یکسان نیست.',
        handoff: 'انتقال با مکان مبدأ و مقصد مشخص ثبت می‌شود؛ دریافت در مقصد مرحله‌ای جداگانه است.',
      },
    ],
  },
  {
    kind: 'cost_center',
    label: 'مرکز هزینه',
    check: 'نام بخش، کد پیشنهادی و وضعیت فعال بودن',
    flows: [
      {
        title: 'از تعریف مرکز هزینه تا درخواست خرید',
        step: 'cost-center',
        steps: [
          { id: 'cost-center', label: 'تعریف مرکز هزینه' },
          { id: 'request', label: 'ثبت درخواست خرید' },
          { id: 'approve', label: 'تأیید درخواست' },
          { id: 'review', label: 'بررسی هزینهٔ همان بخش' },
        ],
        prerequisite: 'نام مرکز هزینه باید با بخش واقعی سازمان هم‌خوان باشد.',
        handoff: 'در درخواست خرید، مرکز هزینه انتخاب می‌شود تا هزینه به همان بخش نسبت داده شود.',
      },
    ],
  },
  {
    kind: 'price_list',
    label: 'لیست قیمت',
    check: 'بازهٔ اعتبار، واحد هر قلم و قیمت ریالی آن',
    flows: [
      {
        title: 'از لیست قیمت تا پیش‌فاکتور',
        step: 'price-list',
        steps: [
          { id: 'product', label: 'تعریف کالا و واحدها' },
          { id: 'price-list', label: 'تعریف لیست قیمت' },
          { id: 'quote', label: 'انتخاب لیست در پیش‌فاکتور' },
          { id: 'order', label: 'تبدیل به سفارش' },
        ],
        prerequisite:
          'کالا و واحدهای فروش آن باید از قبل تعریف شده باشند؛ در لیست فقط واحدهای همان کالا قابل انتخاب است.',
        handoff:
          'در پیش‌فاکتور با انتخاب لیست، قیمت اقلام از آن می‌آید و هر ردیف همچنان قابل ویرایش است.',
      },
    ],
  },
];

const MASTER_FLOWS: Readonly<Record<string, readonly PageHelpFlow[]>> = Object.fromEntries(
  REFERENCES.flatMap((entity) =>
    (['list', 'new', 'edit'] as const).map((mode) => [
      `master-${entity.kind}-${mode}`,
      entity.flows.map((flow, index) =>
        at(
          `master-${entity.kind}-${mode}-${index + 1}`,
          flow.title,
          flow.steps,
          flow.step,
          mode === 'list'
            ? `اینجا ${entity.label} مناسب را از میان رکوردهای موجود پیدا و انتخاب می‌کنی؛ این صفحه محل بررسی فهرست است.`
            : mode === 'new'
              ? `اینجا یک ${entity.label} تازه تعریف می‌کنی تا مرحله‌های بعد بتوانند از آن استفاده کنند.`
              : `اینجا مشخصات همان ${entity.label} موجود را بررسی و در محدودهٔ مجاز ویرایش می‌کنی؛ رکورد تازه‌ای نمی‌سازی.`,
          mode === 'list'
            ? `${flow.prerequisite} برای یافتن مورد درست، ${entity.check} را کنترل کن.`
            : mode === 'new'
              ? `${flow.prerequisite} پیش از ساخت، از نبود رکورد تکراری مطمئن شو.`
              : `${flow.prerequisite} پیش از تغییر، هویت رکورد و ${entity.check} را دوباره بررسی کن.`,
          mode === 'list'
            ? `پس از انتخاب، جزئیات رکورد را بررسی کن یا اگر واقعاً موجود نیست فرم ایجاد را باز کن. ${flow.handoff}`
            : mode === 'new'
              ? `پس از ذخیره و بررسی نتیجه، رکورد در بخش مرتبط قابل انتخاب است. ${flow.handoff}`
              : `پس از ذخیره، مشخصات به‌روز را کنترل کن؛ تغییر این تعریف، ثبت دوبارهٔ تراکنش‌های گذشته نیست. ${flow.handoff}`,
          'support',
          'این نقشه یکی از کاربردهای این تعریف است؛ همهٔ مرحله‌ها برای هر معامله الزامی نیستند. ذخیرهٔ این صفحه به‌تنهایی خرید، فروش، موجودی یا سند حسابداری ثبت نمی‌کند.',
        ),
      ),
    ]),
  ),
);

export const OPERATIONS_PAGE_FLOWS: Readonly<Record<string, readonly PageHelpFlow[]>> = {
  ...MASTER_FLOWS,
  'master-data-home': [
    overview(
      'master-home-sales',
      'آماده‌سازی فروش',
      [{ id: 'base', label: 'اطلاعات پایهٔ مشترک' }, ...SALES.slice(0, 4)],
      'این صفحه نقطهٔ پیدا کردن تعریف‌های مشترک فروش است؛ خودش یک مرحلهٔ صدور سند فروش نیست.',
      'برای شروع فروش، مشتری، کالا و واحد سنجش باید با اطلاعات درست مشخص شده باشند.',
      'پس از آماده‌سازی، مشتری و کالا در پیش‌فاکتور یا سفارش انتخاب می‌شوند.',
    ),
    overview(
      'master-home-purchase',
      'آماده‌سازی خرید و انبار',
      [
        { id: 'base', label: 'تأمین‌کننده، کالا و انبار' },
        { id: 'request', label: 'درخواست خرید' },
        { id: 'order', label: 'سفارش خرید' },
        { id: 'receipt', label: 'رسید کالا' },
        { id: 'stock', label: 'بررسی موجودی' },
      ],
      'این بخش تعریف‌هایی را فراهم می‌کند که خرید و انبار از آن‌ها استفاده می‌کنند.',
      'نام و واحد کالا، هویت تأمین‌کننده و محل نگهداری باید روشن باشد.',
      'عملیات خرید و انبار در صفحهٔ مربوط ثبت می‌شوند؛ تعریف اطلاعات پایه جای آن عملیات را نمی‌گیرد.',
    ),
  ],
  'brand-assets': [
    at(
      'brand-quote-output',
      'از هویت شرکت تا خروجی پیش‌فاکتور',
      [
        { id: 'assets', label: 'تنظیم لوگو، مهر و امضا' },
        { id: 'quote', label: 'تهیهٔ پیش‌فاکتور' },
        { id: 'output', label: 'بررسی خروجی چاپی' },
        { id: 'customer', label: 'ارائه به مشتری' },
      ],
      'assets',
      'این صفحه ظاهر سازمانیِ خروجی پیش‌فاکتور را آماده می‌کند.',
      'تصویرهای تأییدشدهٔ شرکت باید در اختیار فرد دارای مجوز باشد.',
      'تصویرها در خروجی پیش‌فاکتور بررسی می‌شوند؛ این تنظیم سند فروش یا تأیید مالی ایجاد نمی‌کند.',
      'support',
      'تصویر امضا با امضای دیجیتال یا مجوز انجام معامله یکی نیست.',
    ),
  ],
  'crm-home': [
    overview(
      'crm-sales-overview',
      'پیشنهاد فروش تا بررسی مالی',
      SALES,
      'این صفحه نمای کلی مسیر فروش است و مرحلهٔ یک سند مشخص را اعلام نمی‌کند.',
      'مشتری، کالا و اطلاعات پایهٔ لازم باید مشخص باشند؛ مذاکره و پیگیری می‌تواند در سرنخ‌ها انجام شود.',
      'بر اساس نیاز، وارد پیش‌فاکتور، سفارش یا فاکتور مربوط می‌شوی و ادامهٔ کار را همان‌جا دنبال می‌کنی.',
      'سفارش مستقل هم ممکن است بدون پیش‌فاکتور ساخته شود؛ فاکتور کالایی بر پایهٔ تحویل صادر می‌شود.',
    ),
  ],
  'crm-leads': [
    at(
      'lead-sales-development',
      'از آشنایی تا پیشنهاد فروش',
      [
        { id: 'interest', label: 'آشنایی و اعلام علاقه' },
        { id: 'lead', label: 'ثبت و شناخت سرنخ' },
        { id: 'activity', label: 'مذاکره و پیگیری' },
        { id: 'quote', label: 'ارائهٔ پیش‌فاکتور' },
        { id: 'order', label: 'ادامه در سفارش فروش' },
      ],
      'lead',
      'این صفحه جای ثبت فرد یا شرکتی است که احتمال خرید دارد و هنوز نیاز به پیگیری دارد.',
      'نام، راه تماس و دلیل علاقهٔ مخاطب را مشخص کن و پروندهٔ تکراری نساز.',
      'نتیجهٔ مذاکره و مرحلهٔ سرنخ پیگیری می‌شود؛ در ادامه می‌توان آن را به پرونده و اسناد فروش مربوط متصل کرد.',
      'stage',
      'تغییر مرحله یا برنده‌شدن سرنخ به‌تنهایی صدور فاکتور یا وصول پول نیست.',
    ),
  ],
  'crm-activities': [
    at(
      'activity-followup',
      'از برنامهٔ پیگیری تا نتیجه',
      [
        { id: 'context', label: 'انتخاب سرنخ یا مشتری' },
        { id: 'activity', label: 'برنامه‌ریزی و ثبت فعالیت' },
        { id: 'execution', label: 'انجام تماس یا جلسه' },
        { id: 'result', label: 'ثبت نتیجه' },
        { id: 'next', label: 'تعیین پیگیری بعدی' },
      ],
      'activity',
      'این صفحه برنامه و نتیجهٔ کار ارتباطی را نگه می‌دارد.',
      'مخاطب مرتبط، موضوع، مسئول و زمان پیگیری باید مشخص باشند.',
      'پس از انجام واقعی فعالیت، نتیجه و وضعیت آن ثبت می‌شود و اقدام بعدی روشن می‌ماند.',
      'support',
      'ثبت برنامهٔ تماس، خودِ تماس یا فایل ضبط‌شده ایجاد نمی‌کند.',
    ),
  ],
  'crm-quotes': [
    at(
      'quotes-stage',
      'پیش‌فاکتور تا بررسی مالی',
      SALES,
      'quote',
      'این صفحه فهرست و نقطهٔ ثبت پیشنهادهای قیمت است؛ ابتدای مسیر سند فروش را پوشش می‌دهد.',
      'مشتری، کالا، مقدار، قیمت و مهلت اعتبار پیشنهاد را مشخص کن.',
      'برای ارسال، بررسی پذیرش یا ساخت سفارش، وارد جزئیات پیش‌فاکتور می‌شوی.',
      'stage',
      'ثبت پیش‌فاکتور هنوز تحویل کالا، فاکتور قطعی یا دریافت وجه نیست.',
    ),
  ],
  'crm-quote-detail': [
    at(
      'quote-detail-stage',
      'پیش‌فاکتور تا بررسی مالی',
      SALES,
      'quote',
      'این صفحه محل بررسی و ارائهٔ یک پیش‌فاکتور و شروع تبدیل آن به سفارش است.',
      'اقلام، مشتری و مهلت اعتبار پیشنهاد باید کنترل شده باشد؛ پذیرش مشتری را مطابق واقعیت ثبت کن.',
      'تبدیل، یک سفارش فروش می‌سازد؛ رزرو و تحویل و شروع صدور فاکتور در صفحهٔ سفارش دنبال می‌شود.',
      'stage',
      'عنوان تبدیل به سفارش یا فاکتور، مجوز پرش از سفارش و تحویل نیست؛ مسیر متصل، سفارش و تحویل را حفظ می‌کند.',
    ),
  ],
  'crm-orders': [
    at(
      'orders-stage',
      'پیش‌فاکتور تا بررسی مالی',
      SALES,
      'order',
      'این صفحه سفارش‌ها و وضعیت تأمین و تحویل آن‌ها را فهرست می‌کند و محل ثبت سفارش تازه است.',
      'طرف‌حساب و اقلام سفارش باید روشن باشد؛ می‌توان از پیش‌فاکتور استفاده کرد یا سفارش مستقل ساخت.',
      'جزئیات سفارش برای تأیید مجاز، رزرو، تحویل و بررسی فاکتور باز می‌شود.',
      'stage',
      'پیش‌فاکتور برای همهٔ سفارش‌ها اجباری نیست؛ وجود سفارش در فهرست هم به معنی آماده بودن موجودی یا تحویل شدن کالا نیست.',
    ),
  ],
  'crm-order-detail': [
    at(
      'order-detail-sales',
      'جای سفارش در فروش',
      SALES,
      'order',
      'این صفحه پروندهٔ عملیاتی یک سفارش و ارتباط آن با تحویل و فاکتور است.',
      'مشتری و اقلام سفارش باید کنترل شده و وضعیت و دسترسی لازم برای اقدام بعدی فراهم باشد.',
      'رزرو و تحویل واقعی در همین پرونده پیگیری می‌شود؛ صدور فاکتور از مقدار تحویل‌شده شروع می‌شود.',
      'stage',
      'سفارش مستقل می‌تواند بدون پیش‌فاکتور ساخته شود. فاکتور و مدارک مالی مرتبط وضعیت خودشان را دارند؛ مشاهدهٔ این صفحه آن‌ها را قطعی نمی‌کند.',
    ),
    at(
      'order-fulfillment',
      'اجرای سفارش تا صدور فاکتور',
      [
        { id: 'confirmed', label: 'سفارش تأییدشده' },
        { id: 'fulfillment', label: 'رزرو و تحویل سفارش' },
        { id: 'invoice', label: 'صدور فاکتور قابل تحویل' },
        { id: 'finance', label: 'بررسی مدارک مالی' },
      ],
      'fulfillment',
      'بخش اجرایی این صفحه مقدار رزرو، تحویل‌شده و باقی‌ماندهٔ سفارش را به هم مرتبط می‌کند.',
      'کالا، انبار و محل درست و رزرو فعال و مناسبِ سفارش باید بررسی شود.',
      'پس از تحویل واقعی، فقط مقدار قابل صدور در گفت‌وگوی صدور فاکتور بررسی می‌شود.',
      'stage',
      'تحویل جزئی و فاکتور جزئی ممکن است؛ مقدار سفارش، تحویل و فاکتور را یکسان فرض نکن.',
    ),
  ],
  'crm-invoices': [
    at(
      'invoices-stage',
      'فاکتور در مسیر فروش',
      SALES,
      'invoice',
      'این صفحه دفتر جست‌وجو و مشاهدهٔ فاکتورهای فروش است، نه صفحهٔ اجرای تحویل یا ثبت قطعی حسابداری.',
      'فاکتور از مسیر سفارش و مقدار تحویل‌شده ساخته می‌شود؛ برای یافتن آن، مشتری، تاریخ یا شماره را مشخص کن.',
      'وارد جزئیات فاکتور می‌شوی و در صورت نیاز مدارک سفارش و مالی مربوط را بررسی می‌کنی.',
      'support',
      'وضعیت هر فاکتور در خود رکورد مشخص است؛ حضور در این فهرست به معنی قطعی بودن همهٔ فاکتورها نیست.',
    ),
  ],
  'crm-invoice-detail': [
    at(
      'invoice-detail-stage',
      'فاکتور در مسیر فروش',
      SALES,
      'invoice',
      'این صفحه جزئیات یک فاکتور و مرجع فروش آن را برای بررسی نشان می‌دهد.',
      'سفارش، تحویل و فاکتور مربوط باید وجود داشته باشد؛ هویت مشتری و اقلام را کنترل کن.',
      'برای پیگیری ثبت مالی یا اصلاح مجاز، مرجع‌های مرتبط و مسیر مالی مربوط بررسی می‌شوند.',
      'support',
      'این راهنما نقش صفحه را توضیح می‌دهد؛ وضعیت پیش‌نویس، قطعی یا برگشتیِ فاکتور را از خود فاکتور بخوان.',
    ),
  ],
  'crm-returns': [
    at(
      'sales-return-review',
      'پیگیری برگشت پس از فروش',
      [
        { id: 'sale', label: 'فروش و فاکتور مبنا' },
        { id: 'return', label: 'برگشت ثبت‌شده' },
        { id: 'stock', label: 'بررسی برگشت فیزیکی' },
        { id: 'finance', label: 'بررسی اثر مالی' },
        { id: 'followup', label: 'پیگیری تسویهٔ مرتبط' },
      ],
      'return',
      'این صفحه محل مشاهده و بررسی برگشت‌های فروش و سندهای مرتبط آن‌هاست.',
      'فروش مبنا و برگشت مربوط باید مشخص باشد؛ دلیل و مقدار برگشت را با مدارک مقایسه کن.',
      'جزئیات وضعیت برگشت به انبار و مرجع مالی بررسی می‌شود و هر پیگیری عملیاتی در مسیر مجاز خود انجام می‌شود.',
      'support',
      'مشاهدهٔ برگشت، خودش دریافت فیزیکی کالا یا بازپرداخت پول ثبت نمی‌کند.',
    ),
  ],
  'crm-customers': [
    at(
      'customer-select',
      'پروندهٔ مشتری در فروش',
      [
        { id: 'customer', label: 'انتخاب مشتری درست' },
        { id: 'quote', label: 'پیشنهاد قیمت' },
        { id: 'order', label: 'سفارش' },
        { id: 'delivery', label: 'تحویل' },
        { id: 'invoice', label: 'فاکتور' },
      ],
      'customer',
      'اینجا ورودی پرونده‌های مشتری است؛ مشتری درست را پیش از ادامهٔ کار انتخاب می‌کنی.',
      'نام، کد یا شمارهٔ تماس را در اختیار داشته باش و وجود پروندهٔ قبلی را بررسی کن.',
      'پروندهٔ مشتری باز می‌شود تا مشخصات، فروش و ارتباطات همان شخص یا شرکت بررسی شود.',
      'support',
      'پیش‌فاکتور در این نقشه مسیر نمونهٔ فروش است؛ سفارش مستقل هم وجود دارد. ساخت پروندهٔ مشتری، فروش یا ماندهٔ مالی قطعی ایجاد نمی‌کند.',
    ),
  ],
  'crm-customer-detail': [
    at(
      'customer-sales-context',
      'پروندهٔ مشتری در فروش',
      [
        { id: 'customer', label: 'پروندهٔ مشتری' },
        { id: 'quote', label: 'پیش‌فاکتور' },
        { id: 'order', label: 'سفارش' },
        { id: 'delivery', label: 'تحویل' },
        { id: 'invoice', label: 'فاکتور' },
      ],
      'customer',
      'این پرونده نقطهٔ اتصال سوابق فروش همین مشتری است؛ وضعیت هر معامله در سند خودش قرار دارد.',
      'هویت مشتری را با نام و کد کنترل کن و دسترسی هر تب را در نظر بگیر.',
      'از سابقه یا اقدام مجازِ مربوط، وارد پیش‌فاکتور، سفارش یا سند موردنظر می‌شوی.',
      'support',
      'این مسیر نمونهٔ فروشِ دارای پیش‌فاکتور است؛ همهٔ سفارش‌ها الزاماً پیش‌فاکتور ندارند.',
    ),
    at(
      'customer-communications-context',
      'ارتباط و پیگیری مشتری',
      [
        { id: 'customer', label: 'شناسایی مشتری' },
        { id: 'contact', label: 'تماس یا پیام مرتبط' },
        { id: 'review', label: 'بررسی سابقه و تحلیل' },
        { id: 'activity', label: 'فعالیت پیگیری' },
        { id: 'result', label: 'ثبت نتیجه' },
      ],
      'customer',
      'این صفحه سابقهٔ ارتباط را به هویت مشتری متصل می‌کند تا پیگیری جدا از پرونده نماند.',
      'شماره و اطلاعات تماس مشتری باید درست باشد؛ صوت، متن یا تحلیل فقط در صورت وجود و دسترسی قابل بررسی است.',
      'تماس یا پیام مربوط بررسی می‌شود و در صورت نیاز، اقدام بعدی برای همان مشتری ثبت و دنبال می‌شود.',
      'support',
      'این نمودار پیشرفت زندهٔ یک تماس نیست؛ همهٔ مشتریان الزاماً تماس ضبط‌شده یا تحلیل‌شده ندارند.',
    ),
  ],
  'crm-call-base': [
    at(
      'call-base-context',
      'از ثبت تماس تا پیگیری',
      CALL,
      'identify',
      'پایگاه تماس، جای پیدا کردن تماس و بررسی هویت مخاطب و اطلاعات موجود آن است.',
      'تماس باید از منبع متصل دریافت یا فایل معتبر آن بارگذاری شده باشد؛ مخاطب و اپراتور را کنترل کن.',
      'در صورت وجود، صوت و متن و تحلیل بررسی می‌شوند؛ نتیجه در پیگیری سرنخ یا مشتری به‌کار می‌رود.',
      'stage',
      'تحلیل به دسترسی، فایل و اعتبار لازم وابسته است؛ این مسیر اعلام نمی‌کند همهٔ تماس‌ها تمام این مراحل را گذرانده‌اند.',
    ),
  ],
  'crm-sms': [
    at(
      'sms-setup',
      'آماده‌سازی پیامک خودکار',
      [
        { id: 'recipient', label: 'اطلاعات مخاطب' },
        { id: 'configuration', label: 'قالب و روال ارسال' },
        { id: 'trigger', label: 'وقوع شرط روال' },
        { id: 'message', label: 'ارسال از مسیر مجاز' },
        { id: 'history', label: 'بررسی تاریخچه' },
      ],
      'configuration',
      'در تب قالب‌ها و روال‌ها، متن و شرط استفادهٔ پیامک را آماده و کنترل می‌کنی.',
      'شمارهٔ مخاطب، متن موردنظر و دسترسی تنظیم پیامک باید مشخص باشد.',
      'روال فعال در شرایط مربوط از مسیر ارسال استفاده می‌کند؛ نتیجه از تاریخچه بررسی می‌شود.',
      'support',
      'ساخت قالب، به‌تنهایی ارسال نیست؛ فعال کردن روال بر ارسال‌های بعدی اثر دارد.',
    ),
    at(
      'sms-history',
      'پیگیری نتیجهٔ پیامک',
      [
        { id: 'configuration', label: 'قالب و روال' },
        { id: 'message', label: 'پیام ثبت‌شده' },
        { id: 'history', label: 'تاریخچهٔ مشتری' },
        { id: 'followup', label: 'بررسی و پیگیری نتیجه' },
      ],
      'history',
      'در تب تاریخچه، نقش این صفحه مشاهدهٔ پیام‌های مربوط به مشتری است.',
      'مشتری درست را انتخاب کن و وضعیت پیام ثبت‌شده را بررسی کن.',
      'اگر پیام نیاز به بررسی یا پیگیری دارد، موضوع را در مسیر مربوط دنبال می‌کنی.',
      'support',
      'وضعیت تحویل یا شکست را از خود پیام بخوان؛ نمایش تاریخچه تضمین ارسال موفق نیست.',
    ),
  ],
  'crm-my-sales': [
    at(
      'my-sales-commission',
      'فروش، وصول و پورسانت',
      [
        { id: 'invoice', label: 'فاکتور فروش قطعی' },
        { id: 'commission', label: 'ثبت و مشاهدهٔ پورسانت' },
        { id: 'collection', label: 'وصول مبلغ مشتری' },
        { id: 'release', label: 'آزادسازی متناسب پورسانت' },
      ],
      'commission',
      'این صفحه گزارش فروش و پورسانت مربوط به خودت و مبلغ آزادشدهٔ آن را نشان می‌دهد.',
      'فاکتورهای قطعیِ سفارش‌های مربوط به تو و مبنای پورسانت باید در سیستم ثبت شده باشند.',
      'وصول و آزادشدن پورسانت را دنبال می‌کنی؛ این صفحه خودش دریافت پول یا پرداخت پورسانت ثبت نمی‌کند.',
      'support',
      'پورسانت ثبت‌شده با پورسانت آزادشده و پرداخت واقعی آن یکسان نیست.',
    ),
  ],
  'crm-team': [
    overview(
      'team-performance-overview',
      'کنترل عملکرد فروش',
      [
        { id: 'plan', label: 'مسئول و طرح پورسانت' },
        { id: 'activity', label: 'تماس و پیگیری' },
        { id: 'invoice', label: 'فروش قطعی' },
        { id: 'collection', label: 'وصول' },
        { id: 'commission', label: 'بررسی پورسانت و عملکرد' },
      ],
      'این صفحه نمای مدیریتی چند مرحله است؛ یک مرحلهٔ انجام‌شده برای همهٔ فروشندگان را اعلام نمی‌کند.',
      'بازهٔ یکسان و دامنهٔ دادهٔ مجاز را برای مقایسه انتخاب کن.',
      'برای بررسی علت تفاوت شاخص‌ها، پروفایل فروشنده و مدارک مرتبط همان بازه را باز می‌کنی.',
      'تغییر طرح پورسانت، در صورت دسترسی، تنظیم جداگانه است؛ مشاهدهٔ گزارش، پولی پرداخت نمی‌کند.',
    ),
  ],
  'crm-salesperson': [
    overview(
      'salesperson-sales-overview',
      'فروش و پورسانت این فروشنده',
      [
        { id: 'activity', label: 'پیگیری مشتری' },
        { id: 'order', label: 'سفارش مرتبط' },
        { id: 'invoice', label: 'فاکتور فروش' },
        { id: 'collection', label: 'وصول' },
        { id: 'commission', label: 'پورسانت و عملکرد' },
      ],
      'پروفایل فروشنده، چند مرحلهٔ عملکرد یک نفر را در بازهٔ انتخابی کنار هم نشان می‌دهد.',
      'نام فروشنده و بازهٔ زمانی را کنترل کن؛ هر شاخص فقط از دادهٔ در دسترس همان گزارش ساخته می‌شود.',
      'برای پیگیری یک نتیجه، مدارک یا فعالیت‌های مربوط را بررسی می‌کنی؛ خود گزارش، معامله را جلو نمی‌برد.',
    ),
    overview(
      'salesperson-contact-overview',
      'تماس و پیگیری این فروشنده',
      CALL,
      'تب تماس‌ها یک نمای تحلیلی از ارتباطات این فرد است و مرحلهٔ زندهٔ یک تماس خاص نیست.',
      'تماس‌های منتسب به این فروشنده باید در بازه و محدودهٔ دسترسی گزارش وجود داشته باشند.',
      'برای صوت، متن، تحلیل یا پیگیری مورد مشخص، تماس مربوط در پایگاه تماس بررسی می‌شود.',
      'احساس، نتیجه و ریسک فقط برای تماس‌هایی قابل تحلیل است که تحلیل معتبر دارند.',
    ),
  ],
  'inventory-home': [
    overview(
      'inventory-overview',
      'گردش کالا در انبار',
      STOCK,
      'این صفحه نمای کلی انبار است؛ ورود، موجودی، خروج و کنترل گردش را به هم مرتبط می‌کند.',
      'تعریف کالا، واحد، انبار و مکان باید برای عملیات موردنظر آماده باشد.',
      'بر اساس نیاز وارد موجودی، رسید، خروج، انتقال یا کنترل‌های انبار می‌شوی.',
      'رزرو و انتقال، مسیرهای مرتبط با موجودی‌اند؛ همهٔ کالاها الزاماً به رزرو نیاز ندارند.',
    ),
  ],
  'inventory-stock': [
    at(
      'stock-position',
      'جای موجودی در گردش کالا',
      STOCK,
      'stock',
      'این صفحه ماندهٔ ثبت‌شده و مقدار رزرو یا قابل استفاده را بعد از عملیات انبار نشان می‌دهد.',
      'گردش‌های واقعیِ رسید، خروج، انتقال یا تعدیل باید ثبت شده باشند.',
      'برای بررسی علت مقدار، کاردکس و رزروها را جدا بررسی می‌کنی؛ مشاهدهٔ موجودی مقدار آن را تغییر نمی‌دهد.',
      'support',
      'موجودی ثبت‌شده با شمارش تازهٔ فیزیکی یکی نیست و مقدار قابل استفاده ممکن است از موجودی کل کمتر باشد.',
    ),
  ],
  'inventory-cardex': [
    at(
      'cardex-review',
      'از سند انبار تا بررسی مانده',
      [
        { id: 'source', label: 'اتفاق و مرجع واقعی' },
        { id: 'movement', label: 'ثبت سند انبار' },
        { id: 'balance', label: 'تغییر مانده' },
        { id: 'cardex', label: 'بررسی کاردکس' },
        { id: 'investigate', label: 'پیگیری علت اختلاف' },
      ],
      'cardex',
      'کاردکس صفحهٔ دنبال کردن سابقهٔ مقداری و ریالی یک کالا است.',
      'کالا، انبار و بازهٔ گزارش را مشخص کن؛ گردش‌های ثبت‌شده مبنای گزارش هستند.',
      'از ردیف مشکوک به سند اصلی می‌رسی و علت را بررسی می‌کنی؛ اصلاح احتمالی از فرایند مجاز خودش انجام می‌شود.',
      'support',
      'کاردکس محل دستکاری مستقیم گردش‌های قطعی یا جایگزین شمارش فیزیکی نیست.',
    ),
  ],
  'inventory-receipts': [
    at(
      'manual-receipt-stage',
      'ثبت ورود دستی کالا',
      [
        { id: 'definitions', label: 'آمادگی کالا و محل' },
        { id: 'physical', label: 'دریافت واقعی کالا' },
        { id: 'receipt', label: 'ثبت رسید انبار' },
        { id: 'stock', label: 'بررسی موجودی' },
        { id: 'review', label: 'بررسی سند و مرجع' },
      ],
      'receipt',
      'این صفحه ورود کالا را در یک انبار و محل مشخص ثبت می‌کند.',
      'کالا باید واقعاً دریافت شده و مقدار، انبار، محل و مرجع آن روشن باشد.',
      'سند رسید و اثر آن در موجودی و کاردکس بررسی می‌شود؛ مدارک مالیِ مرتبط در مسیر مجاز مالی پیگیری می‌شوند.',
      'stage',
      'برای دریافت سطریِ مرتبط با سفارش خرید، از دریافتِ همان سفارش خرید استفاده کن؛ نوشتن شمارهٔ سفارش در مرجع رسید دستی، جای ارتباط سطری سفارش را نمی‌گیرد.',
    ),
  ],
  'inventory-issues': [
    at(
      'manual-issue-stage',
      'ثبت خروج دستی کالا',
      [
        { id: 'need', label: 'علت و مرجع خروج' },
        { id: 'physical', label: 'تحویل واقعی کالا' },
        { id: 'issue', label: 'ثبت حوالهٔ خروج' },
        { id: 'stock', label: 'بررسی موجودی مانده' },
        { id: 'review', label: 'بررسی سند و مرجع' },
      ],
      'issue',
      'این صفحه خروج کالا از انبار و محل مربوط را با سند دستی ثبت می‌کند.',
      'تحویل‌گیرنده، کالا، مقدار و محل واقعی خروج باید معلوم باشد و موجودی مجاز بررسی شود.',
      'سند خروج در سوابق انبار و مانده بررسی می‌شود؛ پیگیری مرجع فروش یا مالی در مسیر مرتبط ادامه دارد.',
      'stage',
      'تحویل متصل به سفارش فروش را از پروندهٔ همان سفارش دنبال کن؛ حوالهٔ دستی را معادل اجرای کامل مسیر سفارش فرض نکن.',
    ),
  ],
  'inventory-transfers': [
    at(
      'transfer-stage',
      'انتقال میان دو انبار',
      [
        { id: 'transfer', label: 'تعریف انتقال و دو محل' },
        { id: 'dispatch', label: 'آماده‌سازی و خروج' },
        { id: 'transit', label: 'کالا در راه' },
        { id: 'receive', label: 'تأیید وصول مقصد' },
        { id: 'review', label: 'بررسی ماندهٔ دو طرف' },
      ],
      'transfer',
      'این صفحه پروندهٔ انتقال را می‌سازد و مرحله‌های ارسال و وصول آن را پیگیری می‌کند.',
      'کالا، مقدار و انبار و مکان مبدأ و مقصد باید درست مشخص شده باشند.',
      'انتقال مطابق خروج واقعی جلو می‌رود و دریافت مقصد با اقدام جداگانه تأیید می‌شود.',
      'stage',
      'برجسته بودن انتقال، وضعیت جاری یک انتقال خاص را اعلام نمی‌کند؛ وضعیت هر ردیف را بخوان و کالای در راه را وصول‌شده فرض نکن.',
    ),
  ],
  'inventory-adjustments': [
    at(
      'inventory-adjustment-stage',
      'از مغایرت واقعی تا اصلاح ثبت‌شده',
      [
        { id: 'count', label: 'شمارش واقعی' },
        { id: 'cause', label: 'بررسی علت اختلاف' },
        { id: 'adjustment', label: 'آماده‌سازی اصلاح موجودی' },
        { id: 'confirm', label: 'ثبت قطعی تعدیل' },
        { id: 'review', label: 'کنترل سند و مانده' },
      ],
      'adjustment',
      'اینجا مغایرتِ بررسی‌شده با دلیل مشخص برای اصلاح موجودی ثبت می‌شود.',
      'مقدار واقعی و علت اختلاف باید بررسی شده باشد؛ رسید یا خروج جاافتاده را بدون بررسی با تعدیل نپوشان.',
      'پس از تأیید قطعی، سند و ماندهٔ انبار کنترل می‌شوند؛ اصلاحِ بعدی هم به مسیر مجاز خودش نیاز دارد.',
      'stage',
      'این مسیر با ایجاد جلسهٔ انبارگردانی رسمی فرق دارد؛ ثبت قطعی طبق پیام صفحه قابل بازگشت ساده نیست.',
    ),
  ],
  'inventory-reservations': [
    at(
      'reservation-stage',
      'کنارگذاشتن کالا تا تحویل',
      [
        { id: 'need', label: 'نیاز یا سفارش مشخص' },
        { id: 'reservation', label: 'رزرو مقدار کالا' },
        { id: 'review', label: 'بررسی رزرو فعال' },
        { id: 'delivery', label: 'تحویل یا آزادسازی' },
        { id: 'stock', label: 'بررسی ماندهٔ موجودی' },
      ],
      'reservation',
      'این صفحه مقدار کالا را برای یک مرجع کنار می‌گذارد و رزرو فعال را پیگیری می‌کند.',
      'کالا، انبار، محل، مقدار و مرجع نیاز باید روشن و موجودی مناسب بررسی شده باشد.',
      'در صورت تحویل واقعی، مصرف رزرو با مقدار درست ثبت می‌شود؛ اگر نیاز منتفی شد، رزرو از مسیر آزادسازی بررسی می‌شود.',
      'stage',
      'رزرو با خروج واقعی فرق دارد؛ این کار به‌تنهایی فاکتور فروش صادر نمی‌کند.',
    ),
  ],
  'inventory-count-list': [
    at(
      'count-session-list',
      'انبارگردانی رسمی',
      COUNT,
      'snapshot',
      'این صفحه محل انتخاب جلسهٔ قبلی یا ایجاد جلسهٔ تازه با مبنای مشخص شمارش است.',
      'انبار، تاریخ شمارش و مسئولیت تیم شمارش باید روشن باشد و جلسهٔ مشابه بررسی شود.',
      'وارد برگهٔ جلسه می‌شوی و مقدارهای واقعی را برای محاسبهٔ کسری و اضافی ثبت می‌کنی.',
      'support',
      'ایجاد جلسه به‌تنهایی تأیید کسری و اضافی یا صدور تعدیل نیست.',
    ),
  ],
  'inventory-count-detail': [
    at(
      'count-session-detail',
      'انبارگردانی رسمی',
      COUNT,
      'count',
      'این برگه محل ثبت شمارش و بررسی مغایرت‌های همین جلسه است و اقدام نهایی را هم در خود دارد.',
      'انبار و تاریخ جلسه باید درست باشد؛ مقدار هر قلم را از شمارش واقعی و با واحد همان قلم بگیر.',
      'شمارش ذخیره و مغایرت محاسبه می‌شود؛ پس از بررسی و تأیید مجاز، ثبت نهایی اسناد کسری و اضافی را صادر می‌کند.',
      'stage',
      'این نقشه وضعیت زندهٔ جلسه نیست؛ جلسهٔ نهایی‌شده و جلسهٔ در حال شمارش را از وضعیت خود جلسه تشخیص بده.',
    ),
  ],
  'inventory-reconciliation': [
    at(
      'inventory-reconciliation-preview',
      'بررسی سازگاری سوابق انبار',
      [
        { id: 'sources', label: 'گردش، مانده و سریال‌ها' },
        { id: 'preview', label: 'پیش‌نمایش تطبیق' },
        { id: 'cause', label: 'بررسی علت مغایرت' },
        { id: 'compensation', label: 'جبران مجاز در صورت نیاز' },
        { id: 'recheck', label: 'بررسی نتیجهٔ تطبیق' },
      ],
      'preview',
      'این صفحه ناسازگاری میان سوابق انبار و در صورت انتخاب، دفتر کل را بررسی می‌کند.',
      'محدوده، تاریخ و حساب مربوط باید درست باشد؛ ابتدا گزارش بدون ثبت سند اصلاحی را بررسی کن.',
      'علت مغایرت‌ها بررسی می‌شود؛ اجرای زنده و صدور سند جبرانی فقط در صورت نیاز و دسترسی مجاز انجام می‌شود.',
      'support',
      'این کنترل با شمارش فیزیکی فرق دارد؛ اجرای زنده می‌تواند سند تعدیل قطعی بسازد.',
    ),
  ],
  'inventory-tracking': [
    at(
      'tracking-contract',
      'قواعد ردیابی تا سابقهٔ کالا',
      [
        { id: 'tracking', label: 'تعیین شیوهٔ ردیابی' },
        { id: 'identification', label: 'تعریف بهر یا سریال لازم' },
        { id: 'movement', label: 'ثبت گردش با شناسه' },
        { id: 'history', label: 'پیگیری سابقه' },
        { id: 'review', label: 'کنترل موجودی و انقضا' },
      ],
      'tracking',
      'این صفحه قاعدهٔ ثبت شناسه و الزام‌های کالای ردیابی‌شونده را مشخص می‌کند.',
      'پس از دریافت موفق فهرست سازمان فعلی، کالا و واحد پایه و نیاز به مقدار، بهر یا سریال را بررسی کن.',
      'قرارداد کالا را بررسی کن تا بهر، سریال و گردش بعدی طبق همان قواعد ثبت و پیگیری شوند.',
      'support',
      'کالای دارای سابقه ممکن است برای تغییر ساختاری قفل باشد؛ تغییر قرارداد، بازنویسی تاریخچهٔ قبلی نیست.',
    ),
  ],
  'inventory-lots': [
    at(
      'lot-definition',
      'ردیابی یک محمولهٔ هم‌گروه',
      [
        { id: 'product', label: 'کالای دارای ردیابی بهر' },
        { id: 'lot', label: 'تعریف و بررسی بهر' },
        { id: 'receipt', label: 'ثبت ورود با همان بهر' },
        { id: 'quality', label: 'بررسی وضعیت و انقضا' },
        { id: 'issue', label: 'خروج بهر مجاز' },
      ],
      'lot',
      'اینجا شناسه و تاریخ‌های یک گروه کالا و وضعیت مجاز آن برای پیگیری آماده و بررسی می‌شود.',
      'پس از دریافت موفق فهرست سازمان فعلی، شیوهٔ ردیابی کالا، کد محموله و تاریخ‌های لازم را بررسی کن.',
      'بهر درست را در عملیات مرتبط انتخاب کن و موجودی و وضعیت همان محموله را پیگیری کن.',
      'support',
      'قرنطینه و ترخیص فقط برای موارد مربوط و با مجوز انجام می‌شود؛ همهٔ بهرها یک وضعیت یکسان ندارند.',
    ),
  ],
  'inventory-serials': [
    at(
      'serial-definition',
      'ردیابی هر قلم کالای سریال‌دار',
      [
        { id: 'tracking', label: 'قواعد سریال کالا' },
        { id: 'serial', label: 'ثبت و بررسی سریال‌ها' },
        { id: 'receipt', label: 'ورود قلم مربوط' },
        { id: 'movement', label: 'گردش با همان سریال' },
        { id: 'lineage', label: 'بررسی تاریخچهٔ قلم' },
      ],
      'serial',
      'این صفحه شناسهٔ هر قلم را آماده و سابقه و وضعیت سریال‌های موجود را بررسی می‌کند.',
      'پس از دریافت موفق فهرست سازمان فعلی، کالا و قواعد ردیابی آن را بررسی و شماره‌ها را با کالای واقعی تطبیق بده.',
      'در گردش بعدی همان سریال را انتخاب کن و مسیر جابه‌جایی این قلم را از تاریخچهٔ آن دنبال کن.',
      'support',
      'ثبت گروهی سریال، جای ثبت همهٔ مدارک دریافت یا تحویل واقعی کالا را نمی‌گیرد.',
    ),
  ],
  'inventory-uom': [
    at(
      'unit-conversion',
      'از واحد پایه تا مقدار قابل استفاده',
      [
        { id: 'unit', label: 'واحد و کالای مشخص' },
        { id: 'conversion', label: 'تعریف ضریب تبدیل' },
        { id: 'test', label: 'آزمون مقدار نمونه' },
        { id: 'operation', label: 'استفاده در عملیات' },
        { id: 'review', label: 'بررسی مقدار پایه' },
      ],
      'conversion',
      'اینجا رابطهٔ دقیق واحدها را برای کالای مشخص یا دامنهٔ سراسری آماده می‌کنی.',
      'واحدهای مبدأ و مقصد و واحد پایهٔ کالا باید مشخص باشند؛ تعداد داخل بسته را از واقعیت کالا بگیر.',
      'نتیجه در آزمونگر بررسی می‌شود و مقدار عملیات با ضریب معتبر به واحد پایه تفسیر می‌شود.',
      'support',
      'آزمونگر محاسبه می‌کند؛ به‌تنهایی رسید، حواله یا موجودی تازه‌ای ثبت نمی‌کند.',
    ),
  ],
  'inventory-reports': [
    at(
      'inventory-report-review',
      'از عملیات انبار تا تصمیم تأمین',
      [
        { id: 'movement', label: 'ثبت گردش کالا' },
        { id: 'stock', label: 'مانده و حدود موجودی' },
        { id: 'report', label: 'گزارش و هشدار' },
        { id: 'check', label: 'بررسی علت و محدوده' },
        { id: 'decision', label: 'تصمیم خرید یا پیگیری' },
      ],
      'report',
      'این صفحه خروجی انبار را برای بررسی کمبود، رکود و وضعیت انبارها خلاصه می‌کند.',
      'گردش‌ها و در گزارش سفارش مجدد، حدود موجودی باید ثبت شده باشند؛ دامنهٔ داده را بخوان.',
      'با بررسی کاردکس و نیاز واقعی، دربارهٔ خرید یا پیگیری تصمیم می‌گیری؛ سفارش در مسیر خرید ثبت می‌شود.',
      'support',
      'هشدار یا سفارش پیشنهادی، سفارش خرید ثبت‌شده نیست.',
    ),
  ],
  'inventory-movement-detail': [
    at(
      'movement-document-review',
      'از گردش واقعی تا بررسی سند',
      [
        { id: 'physical', label: 'ورود، خروج یا انتقال واقعی' },
        { id: 'document', label: 'ثبت سند انبار' },
        { id: 'detail', label: 'بررسی جزئیات سند' },
        { id: 'finance', label: 'بررسی ارتباط مالی' },
        { id: 'review', label: 'کنترل گردش و مرجع' },
      ],
      'detail',
      'این صفحه محل بررسی یک سند انبار و اقلام و مدارک مرتبط آن است.',
      'سند یا انتقال مربوط باید وجود داشته باشد؛ نوع، تاریخ، مقدار و محل را با مرجع واقعی تطبیق بده.',
      'بر اساس نوع و وضعیت سند، اقدام مجاز مانند تأیید وصول یا بررسی ثبت مالی انجام می‌شود.',
      'support',
      'وجود دکمهٔ ثبت سند حسابداری به معنی نبودن سند قبلی نیست؛ مرجع و نتیجهٔ ثبت را بررسی کن.',
    ),
    at(
      'movement-transfer-receive',
      'اگر سند از نوع انتقال در راه باشد',
      [
        { id: 'transfer', label: 'ثبت انتقال' },
        { id: 'dispatch', label: 'خروج از مبدأ' },
        { id: 'transit', label: 'کالا در راه' },
        { id: 'receive', label: 'تأیید وصول مقصد' },
        { id: 'review', label: 'بررسی ماندهٔ مقصد' },
      ],
      'receive',
      'برای انتقالی که در راه است، این صفحه می‌تواند محل تأیید وصول مجاز در مقصد باشد.',
      'نوع سند باید انتقال باشد و وضعیت و مجوز دریافت اجازهٔ این اقدام را بدهد؛ وصول واقعی را کنترل کن.',
      'پس از تأیید موفق، نتیجهٔ دریافت و مانده و گردش مرتبط بررسی می‌شود.',
      'stage',
      'این مسیر فقط برای انتقال مربوط است و به رسید، خروج یا تعدیل تعمیم داده نمی‌شود.',
    ),
  ],
  'procurement-home': [
    overview(
      'purchase-overview',
      'از نیاز تا دریافت خرید',
      PURCHASE,
      'این صفحه نمای کلی تأمین است و مرحلهٔ یک درخواست یا سفارش مشخص را اعلام نمی‌کند.',
      'نیاز واقعی و کالا و تأمین‌کنندهٔ مناسب باید قابل تشخیص باشند.',
      'بر اساس مرحلهٔ کار، درخواست، سفارش یا تأمین‌کنندهٔ مربوط بررسی می‌شود.',
      'سفارش مستقل هم ممکن است ثبت شود؛ اعلام نیاز، تأیید و دریافت کالا را یک اتفاق واحد فرض نکن.',
    ),
  ],
  'procurement-requests': [
    at(
      'purchase-request-list',
      'از نیاز تا دریافت خرید',
      PURCHASE,
      'request',
      'این صفحه محل ثبت نیاز خرید و یافتن درخواست‌های قبلی است.',
      'عنوان نیاز، اقلام، مقدار و زمان موردنیاز را مشخص کن و درخواست تکراری را بررسی کن.',
      'وارد جزئیات درخواست می‌شوی تا ارسال و تصمیم مجاز دربارهٔ آن پیگیری شود.',
      'stage',
      'ثبت درخواست به معنی ثبت سفارش به تأمین‌کننده یا افزایش موجودی نیست.',
    ),
  ],
  'procurement-request-detail': [
    at(
      'purchase-request-approval',
      'از نیاز تا دریافت خرید',
      PURCHASE,
      'approval',
      'این صفحه نقطهٔ بررسی یک درخواست، تصمیم مجاز دربارهٔ آن و شروع ساخت سفارش مرتبط است.',
      'اقلام، مقدار، زمان نیاز، مقصد و هدف درخواست باید بررسی شود؛ مرحلهٔ درخواست و مجوز خودت را بخوان.',
      'برای درخواست تأییدشده، در صورت مجوز سفارش خرید مرتبط ساخته می‌شود و ادامهٔ تأمین در سفارش دنبال می‌شود.',
      'stage',
      'تأیید درخواست با تأیید سفارش و دریافت کالا جداست؛ رد درخواست به دلیل روشن نیاز دارد.',
    ),
  ],
  'procurement-orders': [
    at(
      'purchase-order-list',
      'از نیاز تا دریافت خرید',
      PURCHASE,
      'order',
      'این صفحه فهرست سفارش‌های خرید و نقطهٔ ثبت سفارش برای تأمین‌کننده است.',
      'تأمین‌کننده، کالا، مقدار و شرایط سفارش باید مشخص باشد؛ در صورت وجود، درخواست تأییدشده را مرتبط کن.',
      'جزئیات سفارش برای تأیید مجاز، حمل و ثبت دریافتِ اقلام باز می‌شود.',
      'stage',
      'در سفارش مستقل، درخواست خرید مرحلهٔ اجباری نیست؛ سفارش خرید هم به‌تنهایی دریافت فیزیکی کالا یا ثبت همهٔ مدارک بدهی و پرداخت نیست.',
    ),
  ],
  'procurement-order-detail': [
    at(
      'purchase-order-context',
      'جای سفارش در تأمین خرید',
      PURCHASE,
      'order',
      'این صفحه پروندهٔ یک سفارش خرید و محل پیگیری اقلام، تأییدها، حمل و دریافت آن است.',
      'تأمین‌کننده، اقلام، مقدار و مرحلهٔ مجاز سفارش باید کنترل شده باشد.',
      'ارسال بار و دریافت‌های مرتبط با همین سفارش ثبت و مقدار دریافت‌شده و باقی‌مانده بررسی می‌شوند.',
      'stage',
      'سفارش مستقل می‌تواند بدون درخواست خرید ساخته شود. وجود خلاصهٔ مالی یا مرجع مرتبط به معنی ثبت قطعی خودکار همهٔ اسناد مالی نیست.',
    ),
    at(
      'purchase-order-receipt',
      'دریافت مرحله‌ای سفارش خرید',
      [
        { id: 'approval', label: 'سفارش مجاز برای دریافت' },
        { id: 'shipment', label: 'حمل و ارسال بار' },
        { id: 'receipt', label: 'ثبت دریافت اقلام سفارش' },
        { id: 'remaining', label: 'بررسی باقی‌ماندهٔ سفارش' },
        { id: 'review', label: 'کنترل رسید و مدارک مرتبط' },
      ],
      'receipt',
      'بخش دریافتِ این صفحه، رسید را به ردیف‌های همین سفارش خرید مرتبط می‌کند.',
      'کالای رسیده، مقدار هر ردیف، تاریخ، انبار و مکان دریافت را با محمولهٔ واقعی کنترل کن.',
      'رسیدهای مرتبط و مقدارهای دریافت‌شده بررسی می‌شوند و باقی سفارش برای دریافت‌های بعدی مشخص می‌ماند.',
      'stage',
      'دریافت جزئی ممکن است؛ برای کامل‌شدن ظاهری سفارش، مقدار رسیده را بیشتر ثبت نکن.',
    ),
  ],
  'procurement-suppliers': [
    at(
      'supplier-reference',
      'از انتخاب تأمین‌کننده تا دریافت خرید',
      [
        { id: 'supplier', label: 'شناخت تأمین‌کننده' },
        { id: 'request', label: 'بررسی نیاز خرید' },
        { id: 'order', label: 'سفارش خرید' },
        { id: 'receipt', label: 'دریافت کالا' },
        { id: 'review', label: 'پیگیری مدارک خرید' },
      ],
      'supplier',
      'این فهرست طرف‌حساب دارای نقش تأمین‌کننده را برای ادامهٔ خرید آماده و قابل انتخاب می‌کند.',
      'هویت شخص یا شرکت و وجود رکورد قبلی را بررسی کن؛ شاید همان شرکت از قبل مشتری هم باشد.',
      'تأمین‌کنندهٔ درست در سفارش خرید انتخاب و سوابق سفارش‌های او بررسی می‌شود.',
      'support',
      'درخواست خرید برای هر سفارش مستقل اجباری نیست. ثبت سریع تأمین‌کننده فقط پروندهٔ طرف‌حساب را می‌سازد؛ خودش سفارش یا پرداخت ثبت نمی‌کند.',
    ),
  ],
  'projects-home': [
    overview(
      'projects-overview',
      'از تعریف پروژه تا بررسی نتیجه',
      PROJECT,
      'این صفحه چند بخش از مسیر پروژه را کنار هم خلاصه می‌کند و وضعیت یک کار مشخص نیست.',
      'موضوع پروژه، مسئولیت‌ها و موعدهای موردنیاز باید برای تیم روشن باشند.',
      'برای برنامه‌ریزی یا پیگیری، پروژه یا کار مربوط را باز می‌کنی.',
      'پیشرفت پروژه را با وضعیت کارها و نتیجهٔ واقعی بررسی کن؛ خلاصهٔ گزارش خودش وضعیت را تغییر نمی‌دهد.',
    ),
  ],
  'projects-list': [
    at(
      'project-list-definition',
      'از تعریف پروژه تا بررسی نتیجه',
      PROJECT,
      'project',
      'این فهرست ورودی پروژه‌های موجود و نقطهٔ ساخت پروژهٔ تازه است.',
      'نام و هدف قابل تشخیص و موعد موردنیاز پروژه را مشخص کن و پروژهٔ مشابه را بررسی کن.',
      'در جزئیات پروژه، اعضای تیم و کارها مشخص و پیگیری می‌شوند.',
      'stage',
      'تعریف پروژه به‌تنهایی کارها را انجام‌شده نمی‌کند یا همهٔ اعضای سازمان را عضو آن نمی‌کند.',
    ),
  ],
  'project-detail': [
    at(
      'project-team-and-work',
      'از تعریف پروژه تا بررسی نتیجه',
      PROJECT,
      'team',
      'این پرونده محل هماهنگ‌کردن تیم، ساخت کارهای پروژه و بررسی وضعیت کلی آن است.',
      'پروژه باید مشخص باشد؛ اعضای مجاز و مسئولیت آن‌ها و موعدها را بررسی کن.',
      'کار به عضو فعال پروژه واگذار می‌شود و اجرای آن از پروندهٔ کار پیگیری می‌شود؛ نتیجه به بررسی پیشرفت پروژه برمی‌گردد.',
      'stage',
      'بستن یا لغو پروژه تصمیم جدا از انجام یک کار است؛ پیام تأییدِ تغییر وضعیت را بخوان.',
    ),
  ],
  'projects-tasks': [
    at(
      'project-task-list',
      'از تعریف پروژه تا بررسی نتیجه',
      PROJECT,
      'task',
      'این صفحه محل یافتن، تعریف و واگذاری کار و پیگیری وضعیت کارهای پروژه‌هاست.',
      'پروژه، عنوان روشن کار، مسئول مناسب و سررسید را مشخص کن.',
      'برای اجرای دقیق، چک‌لیست، زمان کارکرد و تغییر وضعیت مجاز، جزئیات همان کار بررسی می‌شود.',
      'stage',
      'وجود کار در فهرست به معنی شروع یا پایان آن نیست؛ وضعیت هر کار را جدا بخوان.',
    ),
  ],
  'project-task-detail': [
    at(
      'project-task-execution',
      'از تعریف پروژه تا بررسی نتیجه',
      PROJECT,
      'execution',
      'اینجا اجرای یک کار با چک‌لیست، زمان واقعی و وضعیت آن پیگیری می‌شود.',
      'کار، پروژه، مسئول و موعد باید درست باشد؛ مراحل اجرا و محدودیت‌های تغییر وضعیت را بررسی کن.',
      'نتیجه و وضعیت واقعی کار ثبت می‌شود تا در فهرست کارها و بررسی پیشرفت پروژه دیده شود.',
      'stage',
      'ثبت زمان، تیک یک مرحله یا نوشتن نظر به‌تنهایی معادل انجام کامل کار نیست.',
    ),
  ],
  'projects-todos': [
    at(
      'personal-todo',
      'پیگیری یک کار کوتاه شخصی',
      [
        { id: 'idea', label: 'یادآوری یک نیاز' },
        { id: 'todo', label: 'ثبت کار شخصی' },
        { id: 'execution', label: 'انجام واقعی' },
        { id: 'close', label: 'بستن با نتیجهٔ درست' },
      ],
      'todo',
      'اینجا کار کوتاه شخصی را از ذهن به یک فهرست قابل پیگیری منتقل می‌کنی.',
      'موضوع کوتاه و روشن داشته باش؛ اگر به پروژه، مسئول تیمی یا چک‌لیست نیاز دارد، کار پروژه مناسب‌تر است.',
      'پس از اجرای واقعی، یادداشت را انجام‌شده یا در صورت منتفی‌شدن با وضعیت مناسب می‌بندی.',
      'support',
      'این فهرست شخصی، خودبه‌خود پروژه یا وظیفهٔ رسمی تیمی نمی‌سازد.',
    ),
  ],
  'projects-reminders': [
    at(
      'scheduled-reminder',
      'از موضوع تا یادآوری و پیگیری',
      [
        { id: 'topic', label: 'موضوع قابل پیگیری' },
        { id: 'reminder', label: 'ثبت زمان و کانال یادآور' },
        { id: 'notification', label: 'اعلان در مسیر مجاز' },
        { id: 'followup', label: 'انجام پیگیری واقعی' },
        { id: 'result', label: 'بررسی نتیجه' },
      ],
      'reminder',
      'این صفحه زمان و کانال یادآوری موضوع را مشخص می‌کند.',
      'عنوان، زمان درست و کانال موردنیاز را مشخص کن؛ قابلیت دریافت اعلان دستگاه را هم بررسی کن.',
      'در زمان مقرر، وضعیت یادآور و اعلان را بررسی و کار واقعی را جدا پیگیری می‌کنی.',
      'support',
      'ثبت یادآور تضمین دریافت اعلان روی هر دستگاه یا انجام شدن خودکار موضوع نیست.',
    ),
  ],
  'projects-overdue': [
    at(
      'overdue-followup',
      'رسیدگی به کار عقب‌افتاده',
      [
        { id: 'task', label: 'کار با مسئول و موعد' },
        { id: 'due', label: 'رسیدن موعد' },
        { id: 'overdue', label: 'شناسایی کار بازِ عقب‌افتاده' },
        { id: 'followup', label: 'بررسی با مسئول کار' },
        { id: 'result', label: 'ثبت نتیجهٔ واقعی' },
      ],
      'overdue',
      'اینجا مواردی را برای پیگیری پیدا می‌کنی که موعدشان گذشته ولی هنوز بسته نشده‌اند.',
      'کار باید سررسید و وضعیت مشخص داشته باشد؛ پروژه و مسئول مورد درست را کنترل کن.',
      'در جزئیات کار علت تأخیر را بررسی و نتیجه یا وضعیت مجاز را ثبت می‌کنی.',
      'support',
      'این گزارش کار را خودکار انجام یا لغو نمی‌کند؛ عقب‌افتادگی را با نتیجهٔ واقعی کار یکی ندان.',
    ),
  ],
  'projects-comments': [
    at(
      'project-comment-context',
      'گفت‌وگو در کنار کار',
      [
        { id: 'context', label: 'انتخاب پروژه یا کار' },
        { id: 'comment', label: 'ثبت نظر مرتبط' },
        { id: 'read', label: 'مطالعهٔ همکاران مجاز' },
        { id: 'followup', label: 'پیگیری اقدام لازم' },
        { id: 'update', label: 'ثبت نتیجه در کار مربوط' },
      ],
      'comment',
      'اینجا توضیح یا گفت‌وگو را در سابقهٔ پروژه یا کار درست نگه می‌داری.',
      'نوع پروژه یا کار و رکورد مربوط باید درست انتخاب شده باشد؛ نظرهای قبلی را بخوان.',
      'همکار مجاز نظر را در همان سابقه می‌خواند؛ اگر اقدامی لازم است، مسئول و وضعیت در کار مربوط جدا پیگیری می‌شود.',
      'support',
      'نظر به‌تنهایی مسئول، موعد یا وضعیت پروژه و کار را تغییر نمی‌دهد.',
    ),
  ],
  ...Object.fromEntries(
    (
      [
        ['crm', 'فروش'],
        ['inventory', 'انبار'],
        ['procurement', 'خرید'],
        ['projects', 'پروژه‌ها'],
      ] as const
    ).map(([module, label]) => [
      `${module}-assistant`,
      [
        at(
          `${module}-assistant-support`,
          `کمک تحلیلی در ${label}`,
          [
            { id: 'context', label: 'داده و موضوع مجاز' },
            { id: 'question', label: 'سؤال مرتبط از دستیار' },
            { id: 'answer', label: 'بررسی پاسخ و مبنا' },
            { id: 'decision', label: 'تصمیم و پیگیری انسانی' },
            { id: 'operation', label: 'اقدام در صفحهٔ مربوط' },
          ],
          'question',
          `دستیار ${label} در کنار فرایند کاری کمک تحلیلی می‌دهد؛ خودش مرحلهٔ قطعیِ سند عملیاتی نیست.`,
          'دستیار باید فعال و دسترسی و اعتبار لازم فراهم باشد؛ سؤال را با موضوع و محدودهٔ روشن بنویس.',
          'پاسخ با داده و مدارک بررسی می‌شود؛ برای هر عملیات واقعی، وضعیت و نتیجه در صفحهٔ مربوط دنبال می‌شود.',
          'support',
          'این مسیر جای تأیید مجاز، ثبت سند یا بررسی نتیجهٔ واقعی عملیات را نمی‌گیرد.',
        ),
      ],
    ]),
  ),
};
